import {
  Button,
  FirmProfilePhoto,
  Form,
  FormSubmitButton,
  Input,
  Label,
  SelectFormField,
  TextAreaFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import {
  useUpdateFirm,
  useUpdateFirmProfilePicture,
} from "@/lib/data/react-query";
import { FirmSchema } from "@/lib/data/schemas/firm/FirmSchema";
import { type User } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { Upload } from "lucide-react";
import type { ChangeEvent } from "react";
import { useForm } from "react-hook-form";
import { type z } from "zod";

const FORM_ID = "edit-firm-settings-form";

const formSchema = FirmSchema.pick({
  name: true,
  description: true,
  colorTheme: true,
});

interface EditFirmSettingsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  firm?: User["firm"];
}

export function EditFirmSettingsModal({
  open,
  setOpen,
  firm,
}: EditFirmSettingsModalProps) {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: {
      name: firm?.name ?? "",
      description: firm?.description ?? "",
      colorTheme: firm?.colorTheme ?? "default",
    },
    mode: "onSubmit",
  });
  const updateFirm = useUpdateFirm();
  const updateFirmProfilePicture = useUpdateFirmProfilePicture();
  const isUploadingLogo = updateFirmProfilePicture.isPending;
  const hasFirmLogo = Boolean(firm?.profilePicturePresignedUrlKey);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (!firm) {
      setOpen(false);
      return;
    }
    await updateFirm.mutateAsync({
      ...data,
      dateModified: firm.dateModified,
    });
    setOpen(false);
  };

  const isFormLoading =
    updateFirm.isPending || updateFirmProfilePicture.isPending;

  const handleFirmProfilePictureUpload = async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file || !firm) return;
    try {
      await updateFirmProfilePicture.mutateAsync({
        firm,
        file,
      });
    } catch (error) {
      console.error("Error updating firm logo:", error);
    } finally {
      event.target.value = "";
    }
  };

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title="Edit firm settings"
          description="Make changes to your firm information here. Click save when you're done."
          isDirty={form.formState.isDirty}
          loading={isFormLoading}
          size="3xl"
          footer={
            <div className="flex w-full flex-wrap items-center justify-end gap-2">
              <FormCancelButton
                setOpen={setOpen}
                isDirty={form.formState.isDirty}
              />
              <FormSubmitButton
                submitFormId={FORM_ID}
                loading={isFormLoading}
                disabled={isFormLoading}
              />
            </div>
          }
        >
          <form
            id={FORM_ID}
            className="flex min-h-0 grow flex-col overflow-hidden"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div className="overflow-y-auto">
              <div className="border-border border-b p-6">
                <div className="flex items-start gap-4">
                  <div className="flex flex-col items-center gap-3">
                    <div
                      className={cn(
                        "relative rounded-md",
                        !hasFirmLogo &&
                          "border-primary/40 border border-dashed p-2",
                      )}
                    >
                      <FirmProfilePhoto
                        firmName={firm?.name}
                        firmProfilePicturePresignedUrlKey={
                          firm?.profilePicturePresignedUrlKey ?? undefined
                        }
                        className={cn(
                          "h-24 w-40",
                          !hasFirmLogo && "bg-light-primary text-primary",
                        )}
                        showFallbackLogo={true}
                      />
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleFirmProfilePictureUpload}
                        className="hidden"
                        id="firm-logo-upload"
                        disabled={isUploadingLogo || !firm}
                      />
                      <Label
                        htmlFor="firm-logo-upload"
                        aria-disabled={isUploadingLogo || !firm}
                        className={cn(
                          "focus-visible:ring-primary focus-visible:ring-offset-background absolute inset-0 rounded-md focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden",
                          isUploadingLogo || !firm
                            ? "cursor-not-allowed"
                            : "cursor-pointer",
                        )}
                      >
                        <span className="sr-only">
                          {hasFirmLogo
                            ? "Change firm logo"
                            : "Upload firm logo"}
                        </span>
                      </Label>
                      {!hasFirmLogo && (
                        <div className="text-primary pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1 text-xs font-medium">
                          <Upload className="h-5 w-5" />
                          <span>Upload logo</span>
                        </div>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      asChild
                      disabled={isUploadingLogo || !firm}
                    >
                      <Label
                        htmlFor="firm-logo-upload"
                        className={cn(
                          "flex items-center gap-2",
                          !(isUploadingLogo || !firm) && "cursor-pointer",
                        )}
                      >
                        <Upload className="h-4 w-4" />
                        <span>
                          {hasFirmLogo ? "Change logo" : "Upload logo"}
                        </span>
                      </Label>
                    </Button>
                  </div>
                  <div className="flex flex-col">
                    <Label className="text-sm font-medium">Firm logo</Label>
                    <Typography className="text-muted-foreground text-sm">
                      Use the button below to upload or replace your firm logo.
                    </Typography>
                    <Typography className="text-muted-foreground text-sm">
                      For best results, use a square or landscape image with a
                      black or transparent background.
                    </Typography>
                    {isUploadingLogo && (
                      <Typography className="text-primary text-sm">
                        Uploading...
                      </Typography>
                    )}
                  </div>
                </div>
              </div>
              <div className="p-6">
                <fieldset className={slideoverFieldsetClassName}>
                  <TextFormField
                    register={form.register("name")}
                    label="Firm name*"
                  />
                  <TextAreaFormField
                    register={form.register("description")}
                    textAreaClassName="field-sizing-content max-h-[10lh]"
                    label="Description"
                  />
                  <SelectFormField
                    register={form.register("colorTheme")}
                    label="Color theme"
                    itemsKey="value"
                    formatItemLabel={(item) => item?.label}
                    items={[
                      { value: "default", label: "Default (Blue)" },
                      { value: "orange", label: "Orange" },
                      { value: "green", label: "Green" },
                      { value: "purple", label: "Purple" },
                    ]}
                  />
                </fieldset>
              </div>
            </div>
          </form>
        </AppModal>
      </Form>
    </>
  );
}
